import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createRecommendation, updateRecommendation, deleteRecommendation, getRecommendations } from '.././helper/Api/AdminApi';

const RecommendationListLayer = () => {
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  ];
  const currentMonthName = months[new Date().getMonth()];
  const currentYear = new Date().getFullYear().toString();

  const [recommendations, setRecommendations] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [currentRecommendation, setCurrentRecommendation] = useState(null);

  const [formData, setFormData] = useState({
    month: currentMonthName,
    year: currentYear,
    sense: '',
    libelle: '',
    cours: '',
    assetClass: '',
    comments: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 25, 50, 'All'];
  const [searchTerm, setSearchTerm] = useState('');

  const years = Array.from({ length: 14 }, (_, i) => (2035 - i).toString());

  useEffect(() => {
    fetchRecommendations();
  }, []);

  useEffect(() => {
    if (showModal || showConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, showConfirm]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const fetchedRecommendations = await getRecommendations();
      const monthsEn = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const normalized = (fetchedRecommendations || []).map((item) => {
        const rawMonth = item.Month ?? item.month ?? item.MonthNumber ?? '';

        let monthName = currentMonthName;

        const parsed = parseInt(rawMonth, 10);
        if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 12) {
          monthName = months[parsed - 1];
        } else if (typeof rawMonth === 'string' && rawMonth.trim()) {
          const enIndex = monthsEn.findIndex(m => m.toLowerCase() === rawMonth.toLowerCase());
          if (enIndex !== -1) monthName = months[enIndex];
        }
        return {
          id: item.ID || item.id || item.Id,
          month: monthName,
          year: item.Year || item.year || (new Date().getFullYear()),
          sense: item.Sense || item.sense || '',
          libelle: item.Label || item.libelle || '',
          cours: item.Course || item.cours || '',
          assetClass: item.AssetClass || item.assetClass || '',
          comments: item.Comments || item.comments || '',
        };
      });

      setRecommendations(normalized);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch recommendations");
    } finally {
      setLoading(false);
    }
  };

  const filteredRecommendations = recommendations.filter(rec => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      rec.month?.toLowerCase().includes(searchLower) ||
      rec.year?.toString().includes(searchTerm) ||
      rec.sense?.toLowerCase().includes(searchLower) ||
      rec.libelle?.toLowerCase().includes(searchLower) ||
      rec.cours?.toString().includes(searchTerm) ||
      rec.assetClass?.toLowerCase().includes(searchLower) ||
      rec.comments?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculaions
  const totalItems = filteredRecommendations.length;
  const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));

  const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
  const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;

  const paginatedRecommendations = pageSize === 'All'
    ? filteredRecommendations
    : filteredRecommendations.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let start = currentPage - halfVisible;
      let end = currentPage + halfVisible;

      if (start < 1) {
        start = 1;
        end = maxVisiblePages;
      }

      if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxVisiblePages + 1;
      }

      for (let i = start; i <= end; i++) pages.push(i);
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (pageSize === 'All') {
      setCurrentPage(1);
    }
  }, [pageSize]);

  const toggleExpand = (id, field) => {
    setExpandedItems(prev => ({
      ...prev,
      [`${id}-${field}`]: !prev[`${id}-${field}`]
    }));
  };

  const TruncatedText = ({ text, id, field, maxLines = 2 }) => {
    const isExpanded = expandedItems[`${id}-${field}`];

    return (
      <div>
        <div
          className={`text-wrap ${isExpanded ? '' : 'text-truncate-multiline'}`}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: '1.5',
            maxHeight: isExpanded ? 'none' : `${maxLines * 1.5}em`
          }}
        >
          {text}
        </div>
        {text && text.length > 100 && (
          <button
            type="button"
            className="btn btn-link text-primary-600 text-sm p-0 mt-1 text-decoration-none"
            onClick={() => toggleExpand(id, field)}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>
    );
  };

  const validateForm = () => {
    const newErrors = {};
    const numericRegex = /^\d+(\.\d{1,2})?$/;

    if (!formData.month) newErrors.month = "Please select a Month";
    if (!formData.year) newErrors.year = "Please select a Year";
    if (!formData.sense) newErrors.sense = "Please enter Sense";
    if (!formData.libelle) newErrors.libelle = "Please enter Libelle";

    if (!formData.cours || !numericRegex.test(formData.cours)) {
      newErrors.cours = "Please enter a valid number";
    }

    if (!formData.assetClass) newErrors.assetClass = "Please enter Asset Class";
    if (!formData.comments) newErrors.comments = "Please enter Comments";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const monthIndex = months.indexOf(formData.month) + 1;

      const recommendationData = {
        Month: monthIndex,
        Year: parseInt(formData.year, 10),
        Sense: formData.sense,
        Label: formData.libelle,
        Course: formData.cours,
        AssetClass: formData.assetClass,
        Comments: formData.comments,
      };

      if (currentRecommendation) {
        const response = await updateRecommendation(currentRecommendation.id, recommendationData);

        setRecommendations(
          recommendations.map((rec) =>
            rec.id === currentRecommendation.id ? {
              ...rec, ...{
                month: formData.month,
                year: formData.year,
                sense: formData.sense,
                libelle: formData.libelle,
                cours: formData.cours,
                assetClass: formData.assetClass,
                comments: formData.comments,
              }
            } : rec
          )
        );

        toast.success(response.message || "Recommendation updated successfully!");
      } else {
        const response = await createRecommendation(recommendationData);

        setRecommendations([
          ...recommendations,
          {
            id: response.Data?.ID || response.ID || (recommendations.length + 1),
            month: formData.month,
            year: formData.year,
            sense: formData.sense,
            libelle: formData.libelle,
            cours: formData.cours,
            assetClass: formData.assetClass,
            comments: formData.comments,
          },
        ]);

        toast.success(response.message || "Recommendation created successfully!");
      }

      setShowModal(false);
      setCurrentRecommendation(null);
      setErrors({});
      setFormData({
        month: currentMonthName,
        year: new Date().getFullYear().toString(),
        sense: "",
        libelle: "",
        cours: "",
        assetClass: "",
        comments: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (recommendation) => {
    setCurrentRecommendation(recommendation);
    setFormData({
      month: recommendation.month || currentMonthName,
      year: (recommendation.year || new Date().getFullYear()).toString(),
      sense: recommendation.sense || '',
      libelle: recommendation.libelle || '',
      cours: recommendation.cours || '',
      assetClass: recommendation.assetClass || '',
      comments: recommendation.comments || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await deleteRecommendation(id);
      setRecommendations(recommendations.filter((rec) => rec.id !== id));
      toast.success(response.message || "Recommendation deleted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete recommendation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm text-secondary-light">Show:</span>
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={loading}
              >
                {pageSize === 'All' ? 'All' : pageSize}
              </button>
              <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                {pageSizeOptions.map((option) => (
                  <li key={option}>
                    <button
                      className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                      onClick={() => handlePageSizeChange(option)}
                      disabled={loading}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form className="navbar-search">
            <input
              type="text"
              className="form-control radius-8 h-40-px w-auto"
              name="search"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
        </div>

        <button
          className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          onClick={() => {
            setCurrentRecommendation(null);
            setFormData({ month: currentMonthName, year: currentYear, sense: '', libelle: '', cours: '', assetClass: '', comments: '' });
            setShowModal(true);
          }}
          disabled={loading}
        >
          <Icon icon="ic:baseline-plus" className="icon text-lg line-height-1" />
          Add New Recommendation
        </button>
      </div>

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading Recommendations...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col" className="text-center">Action</th>
                    <th scope="col">Month</th>
                    <th scope="col">Year</th>
                    <th scope="col">Sense</th>
                    <th scope="col">Libelle</th>
                    <th scope="col">Cours</th>
                    <th scope="col">Asset Class</th>
                    <th scope="col">Comments</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedRecommendations.length > 0 ? (
                    paginatedRecommendations.map((rec) => (
                      <tr key={rec.id}>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(rec)}
                              disabled={loading}
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>

                            <button
                              type="button"
                              className="bg-danger-50 text-danger-600 bg-hover-danger-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => {
                                setDeleteId(rec.id);
                                setShowConfirm(true);
                              }}
                              disabled={loading}
                            >
                              <Icon icon="fluent:delete-24-regular" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                        <td>{rec.month}</td>
                        <td>{rec.year}</td>
                        <td>{rec.sense}</td>
                        <td>{rec.libelle}</td>
                        <td>{rec.cours}</td>
                        <td>{rec.assetClass}</td>
                        <td style={{ maxWidth: '300px' }}>
                          <TruncatedText
                            text={rec.comments}
                            id={rec.id}
                            field="comments"
                            maxLines={2}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        {searchTerm ? 'No results found for your search.' : 'No recommendations available.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
              <div className="d-flex align-items-center gap-3">
                <span className="text-secondary-light text-sm">
                  Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                  {Math.min(endIndex, totalItems)} of {totalItems} entries
                </span>
              </div>

              {pageSize !== 'All' && totalPages > 1 && (
                <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-left" className="text-lg" />
                    </button>
                  </li>

                  {getPageNumbers().map((page) => (
                    <li className="page-item" key={page}>
                      <button
                        className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${currentPage === page
                            ? "bg-primary-600 text-white shadow-sm"
                            : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                          }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-right" className="text-lg" />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header p-15 border-bottom">
                <h4 className="modal-title text-xl fw-semibold text-primary-light">
                  {currentRecommendation ? 'Edit Recommendation' : 'Add New Recommendation'}
                </h4>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} disabled={loading}></button>
              </div>

              <div className="modal-body p-24">
                <div className="row mb-4">
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Month <span className="text-danger-600">*</span>
                    </label>
                    <div className="dropdown">
                      <button
                        className="form-control radius-8 border border-gray-400 bg-white text-primary-light fw-medium focus:outline-none focus:ring-2 focus:ring-primary-600 text-start d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        disabled={loading}
                        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                      >
                        {formData.month}
                        <Icon icon="akar-icons:chevron-down" className="ms-2 text-primary-600" />
                      </button>

                      <ul className="dropdown-menu w-100 border border-gray-300 shadow-lg" style={{ borderRadius: '8px' }}>
                        {months.map((month, index) => (
                          <li key={month}>
                            <button
                              className="dropdown-item text-primary-light fw-medium py-2"
                              type="button"
                              onClick={() => setFormData({ ...formData, month })}
                              disabled={loading}
                              style={{
                                borderBottom: index < months.length - 1 ? '1px solid #e5e7eb' : 'none',
                                backgroundColor: 'transparent',
                                transition: 'background-color 0.2s ease',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              {month}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {errors.month && <span className="text-danger-600 text-sm mt-1">{errors.month}</span>}
                  </div>

                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Year <span className="text-danger-600">*</span>
                    </label>
                    <div className="dropdown">
                      <button
                        className="form-control radius-8 border border-gray-400 bg-white text-primary-light fw-medium focus:outline-none focus:ring-2 focus:ring-primary-600 text-start d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        disabled={loading}
                        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                      >
                        {formData.year}
                        <Icon icon="akar-icons:chevron-down" className="ms-2 text-primary-600" />
                      </button>

                      <ul className="dropdown-menu w-100 border border-gray-300 shadow-lg" style={{ borderRadius: '8px' }}>
                        {years.map((year, index) => (
                          <li key={year}>
                            <button
                              className="dropdown-item text-primary-light fw-medium py-2"
                              type="button"
                              onClick={() => setFormData({ ...formData, year })}
                              disabled={loading}
                              style={{
                                borderBottom: index < years.length - 1 ? '1px solid #e5e7eb' : 'none',
                                backgroundColor: 'transparent',
                                transition: 'background-color 0.2s ease',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              {year}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {errors.year && <span className="text-danger-600 text-sm mt-1">{errors.year}</span>}
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Sense <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="sense"
                      value={formData.sense}
                      onChange={handleInputChange}
                      placeholder="Enter Sense"
                      disabled={loading}
                    />
                    {errors.sense && <span className="text-danger-600 text-sm mt-1">{errors.sense}</span>}
                  </div>

                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Libelle <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="libelle"
                      value={formData.libelle}
                      onChange={handleInputChange}
                      placeholder="Enter Libelle"
                      disabled={loading}
                    />
                    {errors.libelle && <span className="text-danger-600 text-sm mt-1">{errors.libelle}</span>}
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Cours <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="cours"
                      value={formData.cours}
                      onChange={handleInputChange}
                      placeholder="Enter Cours (e.g., 123.45)"
                      disabled={loading}
                    />
                    {errors.cours && <span className="text-danger-600 text-sm mt-1">{errors.cours}</span>}
                  </div>

                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Asset Class <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="assetClass"
                      value={formData.assetClass}
                      onChange={handleInputChange}
                      placeholder="Enter Asset Class"
                      disabled={loading}
                    />
                    {errors.assetClass && <span className="text-danger-600 text-sm mt-1">{errors.assetClass}</span>}
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-12 mb-4">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                      Comments <span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                      name="comments"
                      value={formData.comments}
                      onChange={handleInputChange}
                      placeholder="Enter Comments"
                      rows="4"
                      disabled={loading}
                    />
                    {errors.comments && <span className="text-danger-600 text-sm mt-1">{errors.comments}</span>}
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-center gap-3 mt-5">
                  <button
                    type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Close
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : currentRecommendation ? 'Update' : 'Add'}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirm && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.2)" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content border-0 radius-12 shadow-lg p-24">

              <h6 className="text-center fw-semibold mb-12">Are you sure?</h6>
              <p className="text-center text-secondary-light mb-20" style={{ fontSize: "14px" }}>
                This action cannot be undone.
              </p>

              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-light px-20 py-6 radius-8"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-danger px-20 py-6 radius-8"
                  onClick={() => {
                    setShowConfirm(false);
                    handleDelete(deleteId);
                  }}
                >
                  Confirm
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendationListLayer;